// AI Video Interview Service - Handles AI-powered video interviews
import AsyncStorage from '@react-native-async-storage/async-storage';

class AIVideoInterviewService {
  constructor() {
    this.isInitialized = false;
    this.currentSession = null;
    this.questions = [];
    this.responses = [];
    this.currentQuestionIndex = 0;
  }

  // Initialize video interview
  async initializeVideoInterview(jobInfo, candidateInfo) {
    try {
      console.log('🎥 Initializing AI Video Interview...');
      
      const session = {
        interviewId: `interview_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        candidateId: candidateInfo.candidateId,
        jobId: jobInfo.jobId,
        jobTitle: jobInfo.title,
        company: jobInfo.company,
        interviewType: 'ai_video',
        startTime: new Date().toISOString(),
        status: 'initialized',
        questions: this.generateQuestions(jobInfo),
        responses: [],
        settings: {
          maxQuestions: 5,
          timePerQuestion: 180, // 3 minutes
          totalDuration: 900 // 15 minutes
        }
      };

      this.currentSession = session;
      this.questions = session.questions;
      this.isInitialized = true;

      console.log('✅ AI Video Interview initialized successfully');
      return session;
    } catch (error) {
      console.error('❌ Failed to initialize AI Video Interview:', error);
      throw error;
    }
  }

  // Generate interview questions based on job info
  generateQuestions(jobInfo) {
    const baseQuestions = [
      {
        id: 'q1',
        question: 'Tell me about yourself and why you\'re interested in this position.',
        type: 'introduction',
        timeLimit: 180,
        category: 'general'
      },
      {
        id: 'q2',
        question: `What experience do you have with ${jobInfo.skills?.[0] || 'the technologies'} mentioned in this role?`,
        type: 'technical',
        timeLimit: 180,
        category: 'skills'
      },
      {
        id: 'q3',
        question: 'Describe a challenging project you worked on and how you overcame the difficulties.',
        type: 'behavioral',
        timeLimit: 180,
        category: 'problem_solving'
      },
      {
        id: 'q4',
        question: `Why do you want to work at ${jobInfo.company}?`,
        type: 'motivation',
        timeLimit: 180,
        category: 'company_fit'
      },
      {
        id: 'q5',
        question: 'Where do you see yourself in 5 years, and how does this role fit into your career goals?',
        type: 'career',
        timeLimit: 180,
        category: 'future_goals'
      }
    ];

    return baseQuestions;
  }

  // Start video interview
  async startVideoInterview() {
    try {
      if (!this.currentSession) {
        throw new Error('Interview session not initialized');
      }

      this.currentSession.status = 'in_progress';
      this.currentSession.actualStartTime = new Date().toISOString();

      return {
        success: true,
        firstQuestion: this.questions[0],
        sessionId: this.currentSession.interviewId
      };
    } catch (error) {
      console.error('❌ Failed to start video interview:', error);
      throw error;
    }
  }

  // Start recording response
  async startRecordingResponse(questionId) {
    try {
      const response = {
        questionId,
        startTime: new Date().toISOString(),
        status: 'recording'
      };

      // Add to responses array
      this.responses.push(response);

      console.log(`🎬 Started recording response for question: ${questionId}`);
      return { success: true, responseId: response.startTime };
    } catch (error) {
      console.error('❌ Failed to start recording:', error);
      throw error;
    }
  }

  // Stop recording response
  async stopRecordingResponse() {
    try {
      const currentResponse = this.responses[this.responses.length - 1];
      if (currentResponse) {
        currentResponse.endTime = new Date().toISOString();
        currentResponse.status = 'completed';
        currentResponse.duration = new Date(currentResponse.endTime) - new Date(currentResponse.startTime);

        // Generate mock analysis
        const analysis = this.generateResponseAnalysis(currentResponse);
        currentResponse.analysis = analysis;

        console.log('🎬 Stopped recording response');
        return { success: true, analysis };
      }

      return { success: false, error: 'No active recording found' };
    } catch (error) {
      console.error('❌ Failed to stop recording:', error);
      throw error;
    }
  }

  // Generate response analysis (mock AI analysis)
  generateResponseAnalysis(response) {
    const analysisScores = {
      confidence: Math.floor(Math.random() * 30) + 70, // 70-100
      clarity: Math.floor(Math.random() * 25) + 75, // 75-100
      relevance: Math.floor(Math.random() * 20) + 80, // 80-100
      enthusiasm: Math.floor(Math.random() * 35) + 65 // 65-100
    };

    return {
      scores: analysisScores,
      overallScore: Math.round(Object.values(analysisScores).reduce((a, b) => a + b, 0) / 4),
      feedback: [
        'Good eye contact maintained',
        'Clear articulation',
        'Relevant examples provided'
      ],
      suggestions: [
        'Consider providing more specific examples',
        'Maintain consistent energy throughout'
      ],
      timestamp: new Date().toISOString()
    };
  }

  // Move to next question
  async moveToNextQuestion() {
    try {
      this.currentQuestionIndex++;
      
      if (this.currentQuestionIndex >= this.questions.length) {
        return {
          success: true,
          question: null,
          completed: true
        };
      }

      const nextQuestion = this.questions[this.currentQuestionIndex];
      return {
        success: true,
        question: nextQuestion,
        completed: false
      };
    } catch (error) {
      console.error('❌ Failed to move to next question:', error);
      throw error;
    }
  }

  // Complete interview
  async completeInterview() {
    try {
      if (!this.currentSession) {
        throw new Error('No active interview session');
      }

      this.currentSession.status = 'completed';
      this.currentSession.endTime = new Date().toISOString();
      this.currentSession.responses = this.responses;

      // Generate final analysis
      const finalAnalysis = this.generateFinalAnalysis();

      console.log('🎉 Interview completed successfully');
      return {
        success: true,
        finalAnalysis,
        sessionId: this.currentSession.interviewId
      };
    } catch (error) {
      console.error('❌ Failed to complete interview:', error);
      throw error;
    }
  }

  // Generate final analysis
  generateFinalAnalysis() {
    const responses = this.responses.filter(r => r.analysis);
    
    if (responses.length === 0) {
      return {
        overallScore: 0,
        recommendation: 'not_recommended',
        summary: 'No responses recorded'
      };
    }

    // Calculate averages
    const avgConfidence = responses.reduce((sum, r) => sum + r.analysis.scores.confidence, 0) / responses.length;
    const avgClarity = responses.reduce((sum, r) => sum + r.analysis.scores.clarity, 0) / responses.length;
    const avgRelevance = responses.reduce((sum, r) => sum + r.analysis.scores.relevance, 0) / responses.length;
    const avgEnthusiasm = responses.reduce((sum, r) => sum + r.analysis.scores.enthusiasm, 0) / responses.length;

    const overallScore = Math.round((avgConfidence + avgClarity + avgRelevance + avgEnthusiasm) / 4);

    let recommendation = 'not_recommended';
    if (overallScore >= 85) recommendation = 'strong_consideration';
    else if (overallScore >= 70) recommendation = 'consideration';

    return {
      overallScore,
      recommendation,
      categoryScores: {
        confidence: Math.round(avgConfidence),
        clarity: Math.round(avgClarity),
        relevance: Math.round(avgRelevance),
        enthusiasm: Math.round(avgEnthusiasm)
      },
      summary: this.generateSummary(overallScore, recommendation),
      strengths: this.identifyStrengths(responses),
      improvements: this.identifyImprovements(responses),
      totalResponses: responses.length,
      completedAt: new Date().toISOString()
    };
  }

  // Generate summary text
  generateSummary(score, recommendation) {
    if (score >= 85) {
      return 'Excellent performance with strong communication skills and relevant experience.';
    } else if (score >= 70) {
      return 'Good performance with solid answers and adequate communication skills.';
    } else {
      return 'Performance shows room for improvement in communication and response quality.';
    }
  }

  // Identify strengths
  identifyStrengths(responses) {
    const strengths = [];
    const avgScores = this.calculateAverageScores(responses);

    if (avgScores.confidence >= 80) strengths.push('Strong confidence and presence');
    if (avgScores.clarity >= 80) strengths.push('Clear communication');
    if (avgScores.relevance >= 80) strengths.push('Relevant and focused answers');
    if (avgScores.enthusiasm >= 80) strengths.push('High enthusiasm and motivation');

    return strengths.length > 0 ? strengths : ['Completed all interview questions'];
  }

  // Identify improvements
  identifyImprovements(responses) {
    const improvements = [];
    const avgScores = this.calculateAverageScores(responses);

    if (avgScores.confidence < 70) improvements.push('Build confidence in presentation');
    if (avgScores.clarity < 70) improvements.push('Improve clarity of communication');
    if (avgScores.relevance < 70) improvements.push('Provide more relevant examples');
    if (avgScores.enthusiasm < 70) improvements.push('Show more enthusiasm for the role');

    return improvements.length > 0 ? improvements : ['Continue practicing interview skills'];
  }

  // Calculate average scores
  calculateAverageScores(responses) {
    if (responses.length === 0) return { confidence: 0, clarity: 0, relevance: 0, enthusiasm: 0 };

    return {
      confidence: responses.reduce((sum, r) => sum + r.analysis.scores.confidence, 0) / responses.length,
      clarity: responses.reduce((sum, r) => sum + r.analysis.scores.clarity, 0) / responses.length,
      relevance: responses.reduce((sum, r) => sum + r.analysis.scores.relevance, 0) / responses.length,
      enthusiasm: responses.reduce((sum, r) => sum + r.analysis.scores.enthusiasm, 0) / responses.length
    };
  }

  // Get current session
  getCurrentSession() {
    return this.currentSession;
  }

  // Reset service
  reset() {
    this.currentSession = null;
    this.questions = [];
    this.responses = [];
    this.currentQuestionIndex = 0;
    this.isInitialized = false;
  }
}

export default AIVideoInterviewService;

